import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Bot, 
  Phone, 
  BarChart3, 
  Settings, 
  Users, 
  Building2,
  LogOut,
  ChevronRight,
  Zap,
  MessageSquare,
  Clock,
  TrendingUp,
  Shield
} from 'lucide-react';

const DashboardPage = () => {
  const { user, logout, isAdmin, api } = useAuth();
  const navigate = useNavigate();

  // Fetch workspaces and redirect to user's own workspace first
  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        const response = await api.get('/api/workspaces');
        if (response.data && response.data.length > 0) {
          // Priority: 1) User's own workspace (owner_id matches) 2) Primary workspace 3) First workspace
          const ownWorkspace = response.data.find(ws => ws.owner_id === user?.user_id);
          const primaryWorkspace = response.data.find(ws => ws.is_primary);
          const targetWorkspace = ownWorkspace || primaryWorkspace || response.data[0];
          localStorage.setItem('currentWorkspaceId', targetWorkspace.workspace_id);
          navigate(`/workspace/${targetWorkspace.workspace_id}`, { replace: true });
        }
      } catch (error) {
        console.error('Failed to fetch workspaces:', error);
      }
    };
    
    fetchAndRedirect();
  }, [api, navigate, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const quickStats = [
    { label: 'Active Agents', value: '3', icon: Bot, color: 'text-teal-400' },
    { label: 'Total Calls', value: '247', icon: Phone, color: 'text-violet-400' },
    { label: 'Minutes Used', value: '1,234', icon: Clock, color: 'text-phosphor-400' },
    { label: 'Conversion Rate', value: '34%', icon: TrendingUp, color: 'text-green-400' },
  ];

  const menuItems = [
    { label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard', active: true },
    { label: 'AI Agents', icon: Bot, href: '/dashboard/agents' },
    { label: 'Call Logs', icon: Phone, href: '/dashboard/calls' },
    { label: 'Analytics', icon: BarChart3, href: '/dashboard/analytics' },
    { label: 'Sub-Accounts', icon: Users, href: '/dashboard/sub-accounts' },
    { label: 'Settings', icon: Settings, href: '/settings' },
  ];

  return (
    <div className="min-h-screen bg-space-900" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-space-800/50 border-r border-white/5 flex flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-phosphor-400 flex items-center justify-center">
              <span className="text-space-900 font-bold text-sm">V</span>
            </div>
            <span className="font-display font-semibold text-lg text-white">
              Voxinity<span className="text-teal-400">AI</span>
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                item.active
                  ? 'bg-teal-400/10 text-teal-400'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}

          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</p>
              </div>
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-violet-400/10 hover:text-violet-400 transition-all duration-200"
              >
                <Shield size={20} />
                <span className="font-medium">Admin Panel</span>
              </Link>
            </>
          )}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-violet-400 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <span className="text-white font-medium">{user?.name?.charAt(0) || 'U'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-display font-semibold text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-400">Here's what's happening with your AI agents today.</p>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          {quickStats.map((stat, i) => (
            <div
              key={stat.label}
              className="glass-card rounded-xl p-6 hover:border-white/10 transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <stat.icon className={`${stat.color}`} size={24} />
                <span className="text-xs text-gray-500 uppercase tracking-wider">Today</span>
              </div>
              <p className="text-3xl font-semibold text-white mb-1">{stat.value}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
        >
          <div className="glass-card rounded-xl p-6 hover:border-teal-400/30 transition-all duration-200 cursor-pointer group">
            <div className="w-12 h-12 rounded-xl bg-teal-400/10 flex items-center justify-center mb-4 group-hover:bg-teal-400/20 transition-colors">
              <Bot className="text-teal-400" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Create AI Agent</h3>
            <p className="text-sm text-gray-400 mb-4">Build and deploy a new voice or chat AI agent in minutes.</p>
            <div className="flex items-center text-teal-400 text-sm font-medium group-hover:gap-2 transition-all">
              Get Started <ChevronRight size={16} />
            </div>
          </div>

          <div className="glass-card rounded-xl p-6 hover:border-violet-400/30 transition-all duration-200 cursor-pointer group">
            <div className="w-12 h-12 rounded-xl bg-violet-400/10 flex items-center justify-center mb-4 group-hover:bg-violet-400/20 transition-colors">
              <MessageSquare className="text-violet-400" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Knowledge Base</h3>
            <p className="text-sm text-gray-400 mb-4">Upload documents and train your AI on your business data.</p>
            <div className="flex items-center text-violet-400 text-sm font-medium group-hover:gap-2 transition-all">
              Upload Files <ChevronRight size={16} />
            </div>
          </div>

          <div className="glass-card rounded-xl p-6 hover:border-phosphor-400/30 transition-all duration-200 cursor-pointer group">
            <div className="w-12 h-12 rounded-xl bg-phosphor-400/10 flex items-center justify-center mb-4 group-hover:bg-phosphor-400/20 transition-colors">
              <Zap className="text-phosphor-400" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Connect Integrations</h3>
            <p className="text-sm text-gray-400 mb-4">Link your CRM, calendar, and other tools to your agents.</p>
            <div className="flex items-center text-phosphor-400 text-sm font-medium group-hover:gap-2 transition-all">
              Browse Integrations <ChevronRight size={16} />
            </div>
          </div>
        </motion.div>

        {/* Recent Activity Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
          <div className="text-center py-12">
            <Phone className="mx-auto text-gray-600 mb-4" size={48} />
            <p className="text-gray-400 mb-2">No recent activity</p>
            <p className="text-sm text-gray-500">Create your first AI agent to start receiving calls.</p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default DashboardPage;
